import { FC, ReactNode, useEffect, useState } from 'react';
import { css } from '@emotion/react';
import clsx from 'clsx';

import SvgRender from '@/library/components/general/SvgRender';
import arrow from '@/common/assets/svg/common/arrows/arrow-filters.svg';
import commonVars from '@/common/assets/scss/abstracts/_exports.module.scss';
import libraryVars from '@/library/assets/scss/abstracts/_exports.module.scss';
import { useCompanySettingsDraft } from './draft-context';

type CategoryProps = {
  id: string;
  title: string;
  children: ReactNode;
};

const Category: FC<CategoryProps> = ({ id, title, children }) => {
  const {
    changes,
    isCategoryVisible,
    searchQuery,
    categoryFilter,
    failedCategoryId,
    showChangesOnly
  } = useCompanySettingsDraft();
  const [isExpanded, setIsExpanded] = useState(true);
  const isVisible = isCategoryVisible(id);
  const hasCategoryChanges = changes.some(change => change.categoryId === id);
  const forceExpanded =
    Boolean(searchQuery.trim()) ||
    categoryFilter === id ||
    failedCategoryId === id ||
    (showChangesOnly && hasCategoryChanges);
  const isOpen = forceExpanded || isExpanded;

  useEffect(() => {
    if (failedCategoryId === id) {
      setIsExpanded(true);
      document.getElementById(`company-settings-category-${id}`)?.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  }, [failedCategoryId, id]);

  return (
    <div
      id={`company-settings-category-${id}`}
      className={clsx(
        'company-settings-category d-flex flex-column cmb-12 bg-white ',
        !isVisible && 'd-none'
      )}
      css={css`
        box-shadow: ${hasCategoryChanges ? `0 0 0 1px ${commonVars.goldBell}` : 'none'};
        border-radius: ${libraryVars.borderRadiusXl};
      `}
    >
      <button
        type="button"
        aria-expanded={isOpen}
        className="d-flex align-items-center border-0 bg-primary text-white cursor-pointer user-select-none"
        css={css`
          padding: 0.25rem 0.75rem;
          border-radius: ${libraryVars.borderRadiusLg};
          margin: 0.3rem;
          box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.3);
        `}
        onClick={() => setIsExpanded(current => !current)}
      >
        <SvgRender
          src={arrow}
          className="text-white flex-shrink-0"
          style={{
            width: 10,
            height: 10,
            transform: isOpen ? undefined : 'rotate(180deg)',
            transition: 'transform 0.2s ease'
          }}
        />
        <span className="fw-bold fs-10 text-uppercase cms-6">{title}</span>
      </button>
      {isOpen ? <div className="p-2">{children}</div> : null}
    </div>
  );
};

export default Category;
